import {RoleBasedPermission} from '@hconnect/apiclient'
import {useUpmPlantsContext} from '@hconnect/upm'
import {usePermissionsQuery} from '@hproduce/permissions'
import {useMemo} from 'react'

const permissionMatchesPlant = (
  dataScope: string,
  targetUpmId: string,
  upmIdToPlantId: Map<string, string>,
  upmIdToCountry: Map<string, string>
): boolean => {
  try {
    const parsed = JSON.parse(dataScope) as Record<string, unknown>
    if (typeof parsed.upmId === 'string') {
      return parsed.upmId === targetUpmId
    }
    if (typeof parsed.plantId === 'string') {
      const resolvedUpmId = [...upmIdToPlantId.entries()].find(
        ([, pId]) => pId === parsed.plantId
      )?.[0]
      return resolvedUpmId === targetUpmId
    }
    if (typeof parsed.countryId === 'string') {
      return upmIdToCountry.get(targetUpmId) === parsed.countryId
    }
    // No plant-specific constraint — global/unrestricted scope
    return true
  } catch {
    return false
  }
}

export const useRiskSurveyPermissions = (upmId: string) => {
  const {data: permissions = []} = usePermissionsQuery()
  const upmPlants = useUpmPlantsContext()

  const upmIdToPlantId = useMemo(
    () => new Map(Object.values(upmPlants).map((plant) => [plant.upmId, plant.plantId])),
    [upmPlants]
  )

  const upmIdToCountry = useMemo(
    () => new Map(Object.values(upmPlants).map((plant) => [plant.upmId, plant.country])),
    [upmPlants]
  )

  const permissionTypes = useMemo(() => {
    return permissions.reduce<string[]>((list, permissionDef) => {
      if (permissionMatchesPlant(permissionDef.dataScope, upmId, upmIdToPlantId, upmIdToCountry)) {
        list.push(permissionDef.permissionType)
      }
      return list
    }, [])
  }, [permissions, upmId, upmIdToPlantId, upmIdToCountry])

  return useMemo(
    () => ({
      canView: permissionTypes.includes(RoleBasedPermission.HproduceRoleViewer),
      canManage: permissionTypes.includes(RoleBasedPermission.HproduceRoleQualityManager)
    }),
    [permissionTypes]
  )
}
